import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom';
import './app/globals.css';

const HomePage = lazy(() => import('./app/page'));
const LoginPage = lazy(() => import('./app/login/page'));
const ContactPage = lazy(() => import('./app/contact/page'));
const SettingsPage = lazy(() => import('./app/settings/page'));
const AdminPage = lazy(() => import('./app/rhinomyadmin/page'));
const TournamentsPage = lazy(() => import('./app/tournaments/page'));
const TournamentDetailPage = lazy(() => import('./app/tournaments/[id]/page'));
const DashboardPage = lazy(() => import('./app/view-matches/page'));
const NewMatchPage = lazy(() => import('./app/matches/new/page'));
const CommentaryPage = lazy(() => import('./app/matches/[id]/commentary/page'));
const FootballScoringPage = lazy(() => import('./app/matches/[id]/football/page'));
const LiveScoringPage = lazy(() => import('./app/matches/[id]/score/page'));
const ScorecardPage = lazy(() => import('./app/matches/[id]/scorecard/page'));
const OverlayPage = lazy(() => import('./app/overlay/[matchId]/page'));
const FootballOverlayPage = lazy(() => import('./app/football-overlay/[matchId]/page'));
const NotFoundPage = lazy(() => import('./app/not-found'));

function DynamicRoute({ component: Component, paramName }: { component: React.ComponentType<any>; paramName: string }) {
  const params = useParams();
  return <Component params={{ [paramName]: params[paramName] ?? '' }} />;
}

const apiBase = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
if (apiBase) {
  const nativeFetch = window.fetch.bind(window);
  const rewriteUrl = (value: string | URL) => {
    const url = value.toString();
    return url.startsWith('/api/') ? `${apiBase}${url}` : url;
  };
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string' || input instanceof URL) {
      return nativeFetch(rewriteUrl(input), init);
    }
    if (input instanceof Request && input.url.startsWith(window.location.origin + '/api/')) {
      return nativeFetch(rewriteUrl(new URL(input.url).pathname + new URL(input.url).search), input);
    }
    return nativeFetch(input, init);
  }) as typeof window.fetch;
  const NativeEventSource = window.EventSource;
  window.EventSource = class extends NativeEventSource {
    constructor(url: string | URL, eventSourceInitDict?: EventSourceInit) {
      super(rewriteUrl(url), eventSourceInitDict);
    }
  };
}

function App() {
  return <BrowserRouter><Suspense fallback={<div className="min-h-screen" />}><Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/settings" element={<SettingsPage />} />
    <Route path="/rhinomyadmin" element={<AdminPage />} />
    <Route path="/tournaments" element={<TournamentsPage />} />
    <Route path="/tournaments/:id" element={<DynamicRoute component={TournamentDetailPage} paramName="id" />} />
    <Route path="/view-matches" element={<DashboardPage />} />
    <Route path="/matches/new" element={<NewMatchPage />} />
    <Route path="/matches/:id/commentary" element={<DynamicRoute component={CommentaryPage} paramName="id" />} />
    <Route path="/matches/:id/football" element={<DynamicRoute component={FootballScoringPage} paramName="id" />} />
    <Route path="/matches/:id/score" element={<DynamicRoute component={LiveScoringPage} paramName="id" />} />
    <Route path="/matches/:id/scorecard" element={<DynamicRoute component={ScorecardPage} paramName="id" />} />
    <Route path="/overlay/:matchId" element={<DynamicRoute component={OverlayPage} paramName="matchId" />} />
    <Route path="/football-overlay/:matchId" element={<DynamicRoute component={FootballOverlayPage} paramName="matchId" />} />
    <Route path="*" element={<NotFoundPage />} />
  </Routes></Suspense></BrowserRouter>;
}

createRoot(document.getElementById('root')!).render(<React.StrictMode><App /></React.StrictMode>);
